import { useState, useEffect, ReactNode, FC } from 'react';

import { data as de } from './translations/de-DE';
import { data as fr } from './translations/fr-FR';
import { data as it } from './translations/it-IT';
import { data as es } from './translations/es-ES';
import { data as nl } from './translations/nl-NL';
import { data as beFr } from './translations/be-FR';
import { data as beNl } from './translations/be-NL';
import { LocaleContext } from './context';
import { LocaleData } from '../../models/locale/locale-data';
import { Locales } from '../../models/locale/locales';
import { getTranslation } from '../../utils/translation/get';

const localePaths: Record<Locales, LocaleData> = {
  'de-DE': de,
  'fr-FR': fr,
  'it-IT': it,
  'es-ES': es,
  'nl-NL': nl,
  'be-FR': beFr,
  'be-NL': beNl,
};

// Default app locale set to 'de-DE'
const baseLocale: Locales = 'de-DE';

const isValidLocale = (locale: Locales): boolean => Object.keys(localePaths).includes(locale);

/** The translations for a locale, falling back to the default locale. */
const resolveLocaleData = (locale: Locales): LocaleData | null =>
  localePaths[isValidLocale(locale) ? locale : baseLocale] ?? null;

export const LocaleProvider: FC<{
  children: ReactNode;
  environment: 'sandbox' | 'development' | 'qa' | 'production';
}> = ({ children, environment = import.meta.env.MODE ?? 'production' }) => {
  const [locale, setLocaleState] = useState(getTranslation);
  // Resolved during the FIRST render, not in an effect. The translations are
  // static imports (see above) — already in the bundle, nothing to wait for — so
  // seeding them here costs nothing. Deferring them to an effect pushed `data`
  // to a second commit, which delayed the `resolved_lp` register past the
  // `$pageview` captured in the first commit. Keep this synchronous.
  const [data, setData] = useState<LocaleData | null>(() => resolveLocaleData(locale));

  const setLocale = (newLocale: Locales) => {
    setLocaleState(newLocale);
  };

  // Keeps `data` in step when the visitor switches country in-page. The first
  // render is already covered by the initialiser above, so this is a no-op on
  // mount.
  useEffect(() => {
    const next = resolveLocaleData(locale);
    if (next) setData(next);
    else if (environment !== 'production') {
      console.error(`Locale data for "${locale}" could not be loaded.`);
    }
  }, [locale, environment]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, data }}>{children}</LocaleContext.Provider>
  );
};
